import {useRef} from 'react';

type WheelEventInterceptorArgs = {
  onStart: () => void;
  onEnd: () => void;
};

/**
 * Used to intercept wheel events to determine if a scroll interaction has finished, as there is no wheel-end event.
 * Together with `FPSMonitor` this class is used to measure FPS during scroll interactions throughout the Designer
 */
export class WheelEventInterceptor {
  private WHEEL_END_TIMEOUT_MS = 100;

  private wheelEventEndTimeout: NodeJS.Timeout | null = null;
  private onStart;
  private onEnd;

  constructor({onStart, onEnd}: WheelEventInterceptorArgs) {
    this.onStart = onStart;
    this.onEnd = onEnd;
  }

  private onWheelEnd = () => {
    this.onEnd();
    this.wheelEventEndTimeout = null;
  };

  intercept() {
    if (!this.wheelEventEndTimeout) {
      this.onStart();
      this.wheelEventEndTimeout = setTimeout(() => {
        this.onWheelEnd();
      }, this.WHEEL_END_TIMEOUT_MS);
    } else {
      clearTimeout(this.wheelEventEndTimeout);

      this.wheelEventEndTimeout = setTimeout(() => {
        this.onWheelEnd();
      }, this.WHEEL_END_TIMEOUT_MS);
    }
  }
}

export const useWheelEventInterceptor = (
  args: WheelEventInterceptorArgs
): WheelEventInterceptor => {
  const wheelEventInterceptor = useRef<WheelEventInterceptor | undefined>(
    undefined
  );
  if (!wheelEventInterceptor.current) {
    wheelEventInterceptor.current = new WheelEventInterceptor(args);
  }

  return wheelEventInterceptor.current;
};
